import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Frame } from "@/components/site/Frame";
import { MaskedLines } from "@/components/site/Motion";
import { EASE } from "@/lib/motion";

const fade = (d) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: EASE, delay: d },
});

const HeroFrame = ({ media }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-6%", "6%"]);
  return (
    <motion.div ref={ref} {...fade(0.5)}>
      <Frame {...media} testId="project-hero-media" className="max-h-[88vh]" innerStyle={{ y, scale: 1.14 }} />
    </motion.div>
  );
};

export const ProjectHero = ({ project: p }) => (
  <header className="pt-14 lg:pt-20">
    <div className="px-6 lg:px-12">
      <motion.div {...fade(0.1)} className="label mb-10 flex flex-wrap justify-between gap-4 text-mute">
        <span>Project {p.no} / 04</span>
        <span>{p.category}</span>
      </motion.div>
      <h1 data-testid="project-title" className="font-display text-[clamp(2.6rem,7.8vw,9rem)] font-medium leading-[0.92] tracking-[-0.055em]">
        <span className="sr-only">{p.title}</span>
        <span aria-hidden="true"><MaskedLines lines={p.titleLines} delay={0.15} lineClass={(i) => (i === 1 ? "lg:pl-[10vw]" : "")} /></span>
      </h1>
      <motion.p {...fade(0.45)} data-testid="project-notice" className="mt-10 inline-flex max-w-3xl items-start gap-3 border border-ink/25 px-4 py-3 text-sm leading-relaxed">
        <span className="label mt-0.5 shrink-0 bg-ink px-1.5 py-0.5 text-paper">{p.tag}</span>
        {p.note}
      </motion.p>
      <motion.dl {...fade(0.55)} className="mt-14 grid grid-cols-1 border-t border-ink/15 sm:grid-cols-2 lg:grid-cols-4" data-testid="project-meta">
        {p.meta.map((m) => (
          <div key={m.label} className="border-b border-ink/15 py-5 sm:pr-6">
            <dt className="label mb-2 text-mute">{m.label}</dt>
            <dd className="text-[15px] leading-snug">{m.value}</dd>
          </div>
        ))}
      </motion.dl>
    </div>
    <div className="mt-14 lg:mt-20">
      <HeroFrame media={p.hero} />
    </div>
  </header>
);
